import { QuantitySelectorComponent, setLimitState } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * GW (integration): the cart does NOT let a locked field silence its steppers.
   * A line whose variant went unavailable renders a locked quantity field, and
   * the shopper still has to be able to take that line down. In the cart the
   * authority on whether the steppers may act is `can_update_quantity`, which
   * already renders them `aria-disabled` at the source when it is false.
   * @returns {boolean}
   */
  get respectsInputLock() {
    return false;
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   *
   * GW: `aria-disabled`, never the `disabled` attribute (02-COMPONENTS §1.3).
   * In the cart the way out of a line is the Remove control that sits next to
   * this stepper, so the at-minimum reason points at it (§9's "the − at qty 1
   * becomes a remove affordance" — here it names the remove affordance rather
   * than duplicating it, and either way it stays reachable by keyboard).
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    setLimitState(minusButton, value <= min, this.minimumReason);
    setLimitState(plusButton, effectiveMax !== null && value >= effectiveMax, this.maximumReason);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
